import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { ArrowRight, Check, LockKeyhole, RefreshCw } from "lucide-react";
import { BILLING_PORTAL, checkoutFor, type BillingAccount, type BillingPreview } from "../../shared/billing";
import { api, BILLING_REQUIRED_EVENT, getToken } from "../lib/api";
import { get, set } from "../state/store";
import { boot, stopForBilling } from "../state/sync";
import { Backdrop } from "../themes/Backdrop";
import { resolveTheme } from "../themes/themes";
import { Mark } from "./Logo";
import "../styles/billing.css";
import { readStored } from "../lib/stored";

const isBilling = (code: string | null | undefined) => code === "billing_required" || code === "trial_required";

/** Checks while open, at the access deadline, on return from checkout, and on any 402. */
export function useBillingAccess(enabled: boolean, boardId: string | null) {
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const revision = useRef(0);
  const serverOffset = useRef(0);
  const lock = useCallback(() => {
    stopForBilling();
    set({ phase: "error", bootErrorCode: "billing_required", bootError: "Your board is saved." });
  }, []);
  const check = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    const rev = ++revision.current;
    setPending(true);
    setProblem(null);
    try {
      let selected = boardId;
      selected ??= readStored("cb:board"); // storage may be disabled
      const r = await api<BillingAccount>(`/api/billing${selected ? `?board=${encodeURIComponent(selected)}` : ""}`);
      if (rev !== revision.current || token !== getToken()) return;
      serverOffset.current = typeof r.serverTime === "number" ? r.serverTime - Date.now() : get().serverOffset;
      setAccount(r);
      if (!r.allowed) lock();
      else if (get().phase === "error" && isBilling(get().bootErrorCode)) {
        set({ phase: "boot", bootError: null, bootErrorCode: null });
        void boot();
      }
    } catch {
      if (rev === revision.current && token === getToken()) setProblem("We couldn’t check your subscription. Please try again.");
    } finally {
      if (rev === revision.current) setPending(false);
    }
  }, [boardId, lock]);
  useEffect(() => {
    if (!enabled) { setAccount(null); return; }
    void check();
    // A checkout opens in another tab. Keep checking a locked tab so paid
    // recovery also releases any edits waiting here, even before it is focused.
    const visible = () => { if (!document.hidden || isBilling(get().bootErrorCode)) void check(); };
    const refused = () => { lock(); void check(); };
    const timer = window.setInterval(visible, 60_000);
    window.addEventListener("focus", visible);
    document.addEventListener("visibilitychange", visible);
    window.addEventListener(BILLING_REQUIRED_EVENT, refused);
    return () => {
      revision.current++;
      clearInterval(timer);
      window.removeEventListener("focus", visible);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener(BILLING_REQUIRED_EVENT, refused);
    };
  }, [enabled, check, lock]);
  useEffect(() => {
    if (!enabled || !account?.allowed || account.until === null) return;
    // Server time decides access. A laptop with a fast clock must never create
    // an early lock/reboot loop or continuously re-arm a zero-delay timer.
    const delay = Math.max(5_000, account.until - (Date.now() + serverOffset.current) + 100);
    const timer = window.setTimeout(() => {
      void check();
    }, Math.min(delay, 2_147_000_000));
    return () => clearTimeout(timer);
  }, [enabled, account, check, lock]);
  return { account, pending, problem, check };
}

function Outline({ preview }: { preview: BillingPreview | null | undefined }) {
  const tiles = preview?.tiles ?? [];
  const minX = Math.min(0, ...tiles.map((t) => t.x));
  const minY = Math.min(0, ...tiles.map((t) => t.y));
  const w = Math.max(900, ...tiles.map((t) => t.x + t.w - minX));
  const h = Math.max(560, ...tiles.map((t) => t.y + t.h - minY));
  return <svg className="billing-outline" viewBox={`${minX - 24} ${minY - 24} ${w + 48} ${h + 48}`} aria-hidden="true">
    {preview?.kind === "doc" ? <rect x="220" y="0" width="620" height="560" rx="18" /> : tiles.map((t, i) => <g key={i}>
      <rect x={t.x} y={t.y} width={t.w} height={t.h} rx="14" />
      <rect className="billing-outline-line" x={t.x + 20} y={t.y + 20} width={Math.max(12, Math.min(100, t.w - 40))} height="7" rx="3" />
      <rect className="billing-outline-line" x={t.x + 20} y={t.y + 48} width={Math.max(12, Math.min(160, t.w - 40))} height="12" rx="4" />
    </g>)}
  </svg>;
}

export function BillingLock({ account, pending, problem, check }: ReturnType<typeof useBillingAccess>) {
  const dialog = useRef<HTMLDialogElement>(null);
  const mounted = useRef(false);
  const theme = resolveTheme(account?.preview ? { id: account.preview.themeId } : { id: "aurora" }, { reducedMotion: true });
  const reason = account?.reason;
  const trial = reason === "trial_ended";
  const newTrial = reason === "trial_required";
  const ended = trial || reason === "subscription_ended";
  const payment = reason === "payment_required";
  const title = trial ? "Your free trial has ended" : payment ? "Your payment needs attention" : reason === "paused" ? "Your subscription is paused" : reason === "subscription_ended" ? "Your subscription has ended" : newTrial ? "Your board is ready when you are" : "Your subscription needs attention";
  const body = ended ? "Your board is saved. Continue with Context Board for $12 a month to pick up where you left off." : payment ? "Your board is saved. Update your payment details to pick up where you left off." : newTrial ? "Start your free trial to open your board." : "Your board is saved. Manage your subscription to pick up where you left off.";
  const cta = payment ? "Update payment details" : newTrial ? "Start free trial" : ended ? "Continue with Context Board" : "Manage subscription";
  const href = ended || newTrial ? account?.checkout ?? checkoutFor() : BILLING_PORTAL;
  useEffect(() => {
    mounted.current = true;
    const el = dialog.current;
    if (el && !el.open) el.showModal();
    document.title = "Context Board";
    return () => { mounted.current = false; el?.close(); };
  }, []);
  return <div className="billing-screen" data-mode={theme.def.mode} style={theme.vars as CSSProperties}>
    <div className="billing-background" aria-hidden="true" inert>
      <Backdrop def={theme.def} motion={false} dim={theme.dim} custom={null} pick={0} />
      <div className="billing-board-name">{account?.preview?.name ?? "Context Board"}</div>
      <div className="billing-rail"><i /><i /><i /><i /><i /></div>
      <Outline preview={account?.preview} />
      <div className="billing-command" />
    </div>
    <dialog ref={dialog} className="billing-dialog" aria-labelledby="billing-title" aria-describedby="billing-description" onCancel={(e) => e.preventDefault()} onClose={(e) => { if (mounted.current && e.currentTarget.isConnected && !e.currentTarget.open) e.currentTarget.showModal(); }}>
      <div className="billing-brand"><Mark size={21} /><span>Context Board</span></div>
      <div className="billing-lock-mark"><LockKeyhole size={24} strokeWidth={1.6} /></div>
      <h1 id="billing-title">{title}</h1>
      <p id="billing-description">{body}</p>
      {!newTrial && (
        <ul className="billing-kept" aria-label="Saved for you">
          {["Boards", "Widgets", "Sources and keys"].map((t) => <li key={t}><Check size={13} strokeWidth={2.4} />{t}</li>)}
        </ul>
      )}
      <a className="btn btn-primary billing-continue" href={href} target="_blank" rel="noopener noreferrer" aria-label={`${cta} (opens in a new tab)`}>{cta}<ArrowRight size={16} /></a>
      <p className="billing-plan">{newTrial ? "Free for 3 days, then $12 a month. Card required. Cancel anytime." : payment ? "Opens your billing page at Lemon Squeezy." : "$12 a month. Cancel anytime."}</p>
      <div className="billing-foot">
        <button className="billing-check" onClick={() => void check()} disabled={pending}><RefreshCw size={13} className={pending ? "billing-checking" : ""} />{pending ? "Checking…" : "I've paid, check again"}</button>
        <span aria-hidden>·</span>
        <a className="billing-help" href="mailto:hello@contextbolt.com">Need a hand?</a>
      </div>
      {problem && <p className="billing-problem" role="status">{problem}</p>}
    </dialog>
  </div>;
}
